import { GeneralApiService } from '@api_patient/general_api.service';
import { AnalyticCategories } from '@application/analytic/analytic.service';
import { AnalyticProvider, useAnalyticContext } from '@application/analytic/analytic_context.provider';
import { withTranslation } from '@application/i18n/with_translation';
import { useTitle } from '@application/use_title';
import { Flex } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Async } from '@component/async.component';
import { Shimmer } from '@component/new_shimmer.component';
import { TopBar } from '@component/top_bar.component';
import { ScreenInformationGathering } from '@feature/information-gathering/screens';
import { useRespondentRepresentation } from '@feature/representation/use-respondent-representation';
import { useResolveController, useService } from '@visiba/instantiation';
import { Timer } from '@visiba/std';
import { useEffect, useRef, useState } from 'react';
import { Outlet, Route, Routes, useLocation } from 'react-router';
import styled from 'styled-components';
import { ScreenClosed } from './screen_closed/screen_closed.component';
import { ScreenError } from './screen_error/screen_error.component';
import { ScreenNhsLoginRequired } from './screen_nhs_login_required/screen_nhs_login_required.component';
import { ScreenRedRobinSessionIframe } from './screen_session_iframe/screen_session_iframe.component';
import { Above110Dialog } from './screen_who_is_respondant/_above_110_dialog.component';
import { ScreenWhoIsRespondant } from './screen_who_is_respondant/screen_who_is_respondant.component';
import { getRedRobinInformationGatheringDestination, useInformationGatheringNavigation } from './use-information-gathering-navigation';
import { ViewRedRobinController } from './view_red_robin.controller';

export function ViewRedRobinSkeleton(): JSX.Element {
	return (
		<StyledContainer>
			<Flex gap={tokens.spacing.x3} grow='1'>
				<Shimmer height={40} width='50%' />

				<Flex gap={tokens.spacing.x1}>
					<Shimmer height={250} width='80%' />
					<Shimmer height={30} width='30%' />
				</Flex>
			</Flex>
		</StyledContainer>
	);
}
export function ViewRedRobin(): JSX.Element {
	return <ViewRedRobinContent />;
}

function ViewRedRobinContent(): JSX.Element {
	const controller = useResolveController(ViewRedRobinController);
	const generalApiService = useService(GeneralApiService);
	const analytics = useAnalyticContext();
	const [showCancelButton, setShowCancelButton] = useState(true);
	const KEEP_ALIVE_POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes

	useTitle(withTranslation('View.RedRobin.Title'));

	function handleRedRobinSessionSubmitted() {
		setShowCancelButton(false);
	}

	useEffect(() => {
		return Timer.repeat(() => {
			generalApiService.touchSession();
		}, KEEP_ALIVE_POLLING_INTERVAL);
	}, [generalApiService]);

	function handleAbort() {
		analytics.trackEvent('Clicked abort button');

		controller.goToReceptionHome();
	}

	return (
		<AnalyticProvider category={AnalyticCategories.RedRobin}>
			<Flex grow='1'>
				<TopBar.Content title={withTranslation('View.RedRobin.Title')} onAbort={showCancelButton ? handleAbort : undefined} />
				<Async derive={controller.flowData} fallback={<ViewRedRobinSkeleton />} catch={<ScreenError />}>
					{(flowData) => {
						if (!flowData) {
							return null;
						}

						return (
							<Routes>
								<Route element={<PaddingOutlet />}>
									<Route path='' element={<ScreenRedRobinRoot />} />
									<Route path='information-gathering' element={<RedRobinInformationGathering />} />
									<Route path='closed' element={<ScreenClosed />} />
									<Route path='nhs-login-required' element={<ScreenNhsLoginRequired />} />
									<Route path='error/*' element={<ScreenError />} />
								</Route>
								<Route
									path=':sessionId'
									element={<ScreenRedRobinSessionIframe handleSessionSubmitted={handleRedRobinSessionSubmitted} />}
								/>
							</Routes>
						);
					}}
				</Async>
			</Flex>
		</AnalyticProvider>
	);
}

function RedRobinInformationGathering() {
	const location = useLocation();
	const navigation = useInformationGatheringNavigation();
	const destination = getRedRobinInformationGatheringDestination(location.state);

	if (destination === null) return <ScreenError />;

	return (
		<ScreenInformationGathering
			error={<ScreenError />}
			onComplete={(entry, draft, patientIdentity) => navigation.complete(destination, entry, draft, patientIdentity)}
		/>
	);
}

// Handle represented patients outside Red Robin's age range before starting a session.
function ScreenRedRobinRoot(): JSX.Element | null {
	const controller = useResolveController(ViewRedRobinController);
	const informationGatheringNavigation = useInformationGatheringNavigation();
	const { currentlyRepresentedPatient, isLoadingRepresentablePatients } = useRespondentRepresentation({
		resolveCurrentRepresentation: true,
	});
	const { currentCountry } = controller.getFlowData();
	const hasStartedRef = useRef(false);

	const representedPatientNin = currentlyRepresentedPatient?.IdentityNumber ?? null;
	const isBelowOneYear = representedPatientNin != null && controller.isBelowOneYearOld(representedPatientNin, currentCountry);
	const isAbove110Years =
		currentlyRepresentedPatient != null &&
		controller.isAbove110YearsOld(
			currentlyRepresentedPatient.IdentityNumber,
			currentlyRepresentedPatient.IdentityCode,
			currentlyRepresentedPatient.DateOfBirth,
		);

	useEffect(() => {
		if (!isBelowOneYear || representedPatientNin == null || hasStartedRef.current) {
			return;
		}

		hasStartedRef.current = true;
		void informationGatheringNavigation.start('representative', 'care-navigation', {
			dateOfBirth: '',
			nhsNumber: representedPatientNin,
		});
	}, [informationGatheringNavigation, isBelowOneYear, representedPatientNin]);

	if (isLoadingRepresentablePatients || isBelowOneYear) {
		return <ViewRedRobinSkeleton />;
	}

	if (isAbove110Years) {
		return <Above110Dialog isOpen onClose={() => controller.goToReceptionHome()} onContinue={() => controller.goToMessaging(null)} />;
	}

	return <ScreenWhoIsRespondant />;
}

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	padding: ${tokens.spacing.x2};

	${tokens.breakpointQuery.small} {
		padding: ${tokens.spacing.x3} ${tokens.spacing.x4} ${tokens.spacing.x4};
		max-width: 580px;
		width: 100%;
		margin: 0 auto;
	}
`;
// Ideally we want to remove these.
function PaddingOutlet() {
	return (
		<StyledContainer>
			<Outlet />
		</StyledContainer>
	);
}
